import React, { memo, useEffect, useState } from "react";
import { useTheme } from "styled-components/native";
import styled from "styled-components/native";
import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import { VDBIcons } from "@utils/VDBIcons";
import Banner from "./index";
import { SpaceType } from "../Spacer/types";
import { useGlobalData } from "@redux/slices/globalSlice";
import { VDBText } from "..";
import { useTranslation } from "react-i18next";
import Routes from "@navigations/Routes";
import { navigate } from "@navigations/utils/RootNavigation";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { DeviceEventEmitter, Platform } from "react-native";
import { getDaysLeft } from "@scenes/more/MoreScreen/utils";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { useGetUser } from "@apis/user";
import { useIsFoldable } from "@navigations/utils/FoldableWrapper";
import { useUserAnalytics } from "../../../hooks/useAnalytics";
import { EventName, EventType, ViewType, ViewTypeEnum } from "@utils/analytics";
import { DefaultTheme } from "styled-components/native";
import {
  BannerLink,
  getBannerTextParts,
  hasBannerLink,
  openBannerLink,
} from "./bannerLinks";

const FreeTrialBanner = () => {
  const theme = useTheme();
  const globalData = useGlobalData();
  const { t } = useTranslation();
  const typography = useTypographyStore();
  const { deviceWidth: width } = useIsFoldable();
  const userInfo = useUserInfo();
  const isWeb = Platform.OS === "web";
  const isAdmin = userInfo.isAdmin();
  const currentPlan = userInfo.user.plan_details?.plan;
  const planExpiry = currentPlan?.expiry;
  const daysLeftResult = getDaysLeft(planExpiry);
  const { daysLeft, daysLeftNoDate } =
    typeof daysLeftResult === "string"
      ? { daysLeft: 0, daysLeftNoDate: "" }
      : daysLeftResult;
  const planExpired =
    daysLeft <= 0 || Object.keys(userInfo.user.plan_details).length === 0;
  const textConfig = globalData?.subscription_config?.free_trial_banner;
  const bannerType =
    (daysLeft <=
      globalData?.subscription_config?.free_trial_banner?.error?.show_in_days ||
      planExpired) &&
    currentPlan?.is_trial
      ? "error"
      : "warning";
  const iconColor =
    bannerType === "error" ? theme["core-01"] : theme["warn-01"];
  const bodyColor =
    bannerType === "error" ? theme["core-01"] : theme["core-05"];
  const [userId, setUserId] = useState(userInfo.user.id);
  const {} = useGetUser(userId, setUserId);
  const { trackButtonEvent } = useUserAnalytics();
  const handleLinkPress = () => {
    trackButtonEvent(
      EventName.HYPER_LINK_CLICK,
      EventType.BANNER_LINK_CLICK,
      { component_type: ViewType.BANNER },
      ViewTypeEnum?.PAID_MARKETPLACE_BANNER
    );
    setUserId(userInfo.user.id);
    if (theme.isLargeScreen) {
      navigate(Routes.ExploreNavigator, {
        screen: Routes.AccountOverviewScreen,
      });
    } else {
      navigate(Routes.MoreNavigator, {
        screen: Routes.AccountOverviewUsersSeats,
        initial: false,
      });
    }
    if (
      userInfo.isInExpiryBucketButUpcomingSubscription() &&
      userInfo.getUpcomingSubscription()?.plan_duration === 1
    ) {
      setTimeout(
        () =>
          DeviceEventEmitter.emit("OPEN_MANAGE_PLAN_POPUP", {
            type: "renewal",
            plan_type: 2,
          }),
        200
      );
    }
  };

  const handleBannerLinkPress = (link: BannerLink) => {
    trackButtonEvent(
      EventName.HYPER_LINK_CLICK,
      EventType.BANNER_LINK_CLICK,
      { component_type: ViewType.BANNER },
      ViewTypeEnum?.PAID_MARKETPLACE_BANNER
    );
    setUserId(userInfo.user.id);
    openBannerLink(link, { isLargeScreen: theme.isLargeScreen });
  };

  useEffect(() => {
    trackButtonEvent(
      EventName.VIEW,
      null,
      { component_type: ViewType.BANNER },
      ViewTypeEnum?.PAID_MARKETPLACE_BANNER
    );
  }, []);

  const getText = () => {
    if (planExpired && isWeb && currentPlan?.is_trial) {
      if (isAdmin) {
        if (userInfo.isUpcomingNotPaidTrialPlan()) {
          return t(
            "banner.freeTrial.expired.admin.upcoming.screenText.text",
            "Your Free Trial Has Ended - After payment, {{link}} to update your access."
          );
        }
        return t(
          "banner.freeTrial.expired.admin.screenText.text",
          "Your Free Trial Has Ended — Activate your Membership in the {{link}} to stay connected!"
        );
      } else {
        return t(
          "banner.freeTrial.expired.member.screenText.text",
          "Your Free Trial Has Ended — Please contact your company admin to activate a Membership Plan."
        );
      }
    } else if (currentPlan?.plan_type === "saas_plan") {
      if (currentPlan?.purchased_seats > 0) {
        if (userInfo.isMembershipExpired()) {
          if (isAdmin) {
            return t(
              "banner.saasPlan.purchasedSeats.expired.admin.screenText.text",
              'Your Supplier or Solution plan has expired. Your Marketplace access, including Purchased Additional Users, is now limited. {@link action="openProfile" text="Purchase Plan" showOn="web"}'
            );
          } else {
            return t(
              "banner.saasPlan.purchasedSeats.expired.member.screenText.text",
              "Your Supplier or Solution plan has expired. Your Marketplace access, including Purchased Additional Users, is now limited. Please contact your company admin to purchase a membership plan."
            );
          }
        } else if (userInfo.isInExpiryBucketButUpcomingSubscription()) {
          return t(
            "banner.saasPlan.purchasedSeats.upcoming.screenText.text",
            "Your Supplier or Solution plan renews on {{renewal_date}}. Marketplace access, including Purchased Additional Users, is tied to this plan. Renewal details will be sent separately."
          );
        }
      } else {
        if (userInfo.isMembershipExpired()) {
          if (isAdmin) {
            return t(
              "banner.saasPlan.noPurchasedSeats.expired.admin.screenText.text",
              'Your Supplier or Solution plan has expired. Marketplace access provided through this plan is now limited. {@link action="openProfile" text="Purchase Plan" showOn="web"}'
            );
          } else {
            return t(
              "banner.saasPlan.noPurchasedSeats.expired.member.screenText.text",
              "Your Supplier or Solution plan has expired. Marketplace access provided through this plan is now limited. Please contact your company admin to purchase a membership plan."
            );
          }
        } else if (userInfo.isInExpiryBucketButUpcomingSubscription()) {
          return t(
            "banner.saasPlan.noPurchasedSeats.upcoming.screenText.text",
            "Your Supplier or Solution plan is up for renewal on {{renewal_date}}. Your Marketplace access is tied to this plan. Renewal details will be provided separately."
          );
        }
      }
    } else if (userInfo.isInExpiryBucketButUpcomingSubscription()) {
      if (userInfo.getUpcomingSubscription()?.plan_duration === 2) {
        return t(
          "banner.renewal.twoYear.admin.screenText.text",
          'Your 2-Year renewal is scheduled for {{renewal_date}}. Your saved payment method will be charged automatically on the renewal date. Once payment is complete, {@link action="openProfile" text="click here"} to refresh and update your plan.'
        );
      } else {
        return t(
          "banner.renewal.oneYear.admin.screenText.text",
          'Your 1-Year Marketplace Plan renews on {{renewal_date}} at $360 + any additional users. {@link action="openTwoYearPlanPopup" text="Save 15% with a 2-Year Plan" showOn="web"}'
        );
      }
    } else if (userInfo.isMembershipExpired()) {
      if (isAdmin) {
        return t(
          "banner.expired.membership.admin.screenText.text",
          'Your Marketplace membership has expired. Purchase a plan to restore Full Access. {@link action="openProfile" text="Purchase Plan" showOn="web"}'
        );
      }
      return t(
        "banner.expired.membership.member.screenText.text",
        "Plan Expired! Please contact your company admin to purchase a membership plan."
      );
    }
    switch (bannerType) {
      case "warning":
        if (userInfo.isEnrolledTrial()) {
          if (userInfo.isAdmin()) {
            if (theme.isLargeScreen || isWeb) {
              return t(
                "banner.freeTrial.enrolledTrial.warning.admin.web.text",
                "ACTION REQUIRED: {{days}} left. Visit the {{link}} to review and update your users."
              );
            } else {
              return t(
                "banner.freeTrial.enrolledTrial.warning.admin.mobile.text",
                "ACTION REQUIRED: {{days}} left. Go to your {{link}} to review and update your users."
              );
            }
          } else {
            return t(
              "banner.freeTrial.enrolledTrial.warning.member.text",
              "ACTION REQUIRED: {{days}} left. Please contact your company admin in case of any issues."
            );
          }
        } else if (userInfo.isActiveFreeTrial()) {
          return !isWeb
            ? !isAdmin
              ? t(
                  "banner.freeTrial.mobile.warning.member.screenText.text",
                  "{{days}} Free Trial! Please contact your company admin to purchase a membership plan."
                )
              : isAdmin && userInfo.isUpcomingNotPaidTrialPlan()
              ? t(
                  "banner.freeTrial.mobile.warning.upcoming.member.screenText.text",
                  "{{days}} Free Trial! After payment, {{link}} to update your access."
                )
              : t(
                  `${textConfig?.warning?.mobile?.text}`,
                  "{{days}} Free Trial! Go to your {{link}} to see your account details."
                )
            : !isAdmin
            ? t(
                "banner.freeTrial.web.warning.member.screenText.text",
                "{{days}} Free Trial! Please contact your company admin to purchase a membership plan."
              )
            : isAdmin && userInfo.isUpcomingNotPaidTrialPlan()
            ? t(
                "banner.freeTrial.web.warning.upcoming.member.screenText.text",
                "{{days}} Free Trial! After payment, {{link}} to update your access."
              )
            : t(
                `${textConfig?.warning?.web?.text}`,
                "{{days}} Free Trial!  Please {{link}} to set up a membership plan."
              );
        } else if (userInfo.isMembershipExpired()) {
          if (isAdmin) {
            return t(
              "banner.freeTrial.expired.membership.admin.screenText.text",
              "Your Marketplace membership has expired. Purchase a plan to restore Full Access."
            );
          } else {
            return t(
              "banner.freeTrial.expired.membership.member.screenText.text",
              "Plan Expired! Please contact your company admin to purchase a membership plan."
            );
          }
        }
        return "";
      case "error":
        if (userInfo.isEnrolledTrial()) {
          if (userInfo.isAdmin()) {
            if (theme.isLargeScreen || isWeb) {
              return t(
                "banner.freeTrial.enrolledTrial.error.admin.web.text",
                "FINAL DAYS LEFT: {{days}} remaining. Visit the {{link}} to review and update your users."
              );
            } else {
              return t(
                "banner.freeTrial.enrolledTrial.error.admin.mobile.text",
                "FINAL DAYS LEFT: {{days}} remaining. Go to your {{link}} to review and update your users."
              );
            }
          } else {
            return t(
              "banner.freeTrial.enrolledTrial.error.member.text",
              "FINAL DAYS LEFT: {{days}} remaining. Please contact your company admin in case of any issues."
            );
          }
        } else if (userInfo.isActiveFreeTrial()) {
          return !isWeb
            ? !isAdmin
              ? t(
                  "banner.freeTrial.mobile.error.member.screenText.text",
                  "{{days}} Free Trial! Please contact your company admin to purchase a membership plan."
                )
              : isAdmin && userInfo.isUpcomingNotPaidTrialPlan()
              ? t(
                  "banner.freeTrial.web.warning.upcoming.member.screenText.text",
                  "{{days}} Free Trial! After payment, {{link}} to update your access."
                )
              : t(
                  `${textConfig?.error?.mobile?.text}`,
                  "{{days}} Free Trial! Go to your {{link}} to see your account details."
                )
            : !isAdmin
            ? t(
                "banner.freeTrial.web.error.member.screenText.text",
                "{{days}} Free Trial! Please contact your company admin to purchase a membership plan."
              )
            : isAdmin && userInfo.isUpcomingNotPaidTrialPlan()
            ? t(
                "banner.freeTrial.web.warning.upcoming.member.screenText.text",
                "{{days}} Free Trial! After payment, {{link}} to update your access."
              )
            : t(
                `${textConfig?.error?.web?.text}`,
                "{{days}} Free Trial!  Please {{link}} to set up a membership plan."
              );
        }
        return "";
    }
  };
  const text = getText();
  const message = t(text, {
    days: daysLeftNoDate,
    renewal_date: userInfo.planExpiryDetails()?.expiry_date
      ? getDaysLeft(userInfo.planExpiryDetails()?.expiry_date)?.renewalDate ??
        null
      : "",
  });
  const TextContent = () => {
    if (hasBannerLink(message)) {
      // Phones read better left aligned. Tablets and desktop stay centred.
      const alignLeft = !!theme.isMobile;
      return (
        <LinkedBannerRow alignLeft={alignLeft}>
          <VDBIcon
            variant={IconsVariant.VDBCustom}
            name={VDBIcons.WARNING}
            color={iconColor}
            size={24}
          />
          <LinkedBannerTextArea alignLeft={alignLeft}>
            <VDBText
              color={bodyColor}
              type={typography["Body-M"]}
              textAlign={alignLeft ? "left" : "center"}
            >
              {getBannerTextParts(message, isWeb).map((part, index) =>
                part.kind === "link" ? (
                  <StyledLinkText
                    key={index}
                    bannerType={bannerType}
                    onPress={() => handleBannerLinkPress(part.link)}
                  >
                    {part.link.text}
                  </StyledLinkText>
                ) : (
                  <React.Fragment key={index}>{part.text}</React.Fragment>
                )
              )}
            </VDBText>
          </LinkedBannerTextArea>
        </LinkedBannerRow>
      );
    }
    const messageParts = message.includes("{{link}}")
      ? message.split("{{link}}")
      : [message, ""];
    const firstPart =
      currentPlan?.is_trial && planExpired
        ? messageParts[0].replace(
            `${daysLeftNoDate} Free Trial!`,
            "Free Trial Ended!"
          )
        : !isWeb && messageParts[0]
        ? messageParts[0].replace(" Free Trial!", " Free Trial!\n")
        : messageParts[0];
    const trialMatch = messageParts[0].match(
      /-?\d+\s*Day[s]?\s*Free\s*Trial!?/i
    );
    const trialText = trialMatch ? trialMatch[0] : messageParts[0];
    const remainingText =
      messageParts[0].replace(trialText, "").trim() + messageParts[1];
    const remainingTextParts = remainingText.replace(
      userInfo.isUpcomingNotPaidTrialPlan() ? "After payment," : "Go to your",
      userInfo.isUpcomingNotPaidTrialPlan()
        ? "After payment, {{link}}"
        : "Go to your {{link}}"
    );
    const remainingTextPartsMobile = remainingText.replace(
      "Please",
      "Please {{link}}"
    );
    const firstPartText = planExpired
      ? currentPlan?.is_trial
        ? "Your Free Trial Has Ended!"
        : t(
            "banner.expired.membership.screenText.text",
            "Your Marketplace membership has expired. Purchase a plan to restore Full Access."
          )
      : trialText;
    const MobileContent = () => {
      if (userInfo.isEnrolledTrial() && isAdmin) {
        const enrolledFirstPart = messageParts[0].split("Go to your")[0].trim();
        return (
          <MobileContainer>
            <FlexContainer>
              <VDBIcon
                variant={IconsVariant.VDBCustom}
                name={VDBIcons.WARNING}
                color={iconColor}
                size={24}
              />
              <VDBText color={bodyColor} type={typography["Body-M"]}>
                {enrolledFirstPart}
              </VDBText>
            </FlexContainer>
            <VDBText
              color={bodyColor}
              type={typography["Body-M"]}
              textAlign="center"
            >
              {"Go to your "}
              <StyledLinkText bannerType={bannerType} onPress={handleLinkPress}>
                {t("banner.freeTrial.profileSection.mobile.text", "profile")}
              </StyledLinkText>
              {messageParts[1]}
            </VDBText>
          </MobileContainer>
        );
      }
      return (
        <MobileContainer>
          <FlexContainer>
            <VDBIcon
              variant={IconsVariant.VDBCustom}
              name={VDBIcons.WARNING}
              color={iconColor}
              size={24}
            />
            <VDBText color={bodyColor} type={typography["Body-M"]}>
              {firstPartText}
            </VDBText>
          </FlexContainer>
          {userInfo.isEnrolledTrial() ? null : (
            <VDBText
              color={bodyColor}
              type={typography["Body-M"]}
              textAlign="center"
            >
              {planExpired &&
                !isAdmin &&
                messageParts[0].split(" Free Trial!")[1] + " "}
              {remainingTextParts.split("{{link}}")[0]}
              {isAdmin && (
                <StyledLinkText
                  bannerType={bannerType}
                  onPress={handleLinkPress}
                >
                  {userInfo.isUpcomingNotPaidTrialPlan()
                    ? t(
                        "banner.freeTrial.profileSection.mobile.upcoming.text",
                        "click here"
                      )
                    : userInfo.isActiveFreeTrial()
                    ? t(
                        "banner.freeTrial.profileSection.mobile.text",
                        "profile"
                      )
                    : ""}
                </StyledLinkText>
              )}
              {userInfo.isUpcomingNotPaidTrialPlan() ? "" : " "}
              {remainingTextParts.split("{{link}}")[1]}
            </VDBText>
          )}
        </MobileContainer>
      );
    };

    const WebContent = () => {
      return (
        <VDBText
          color={bodyColor}
          type={typography["Body-M"]}
          textAlign="center"
        >
          <FlexContainer>
            {!theme.isLargeScreen && (
              <VDBIcon
                variant={IconsVariant.VDBCustom}
                name={VDBIcons.WARNING}
                color={iconColor}
                size={24}
              />
            )}
            {theme.isLargeScreen ? firstPart : `${firstPartText}\n`}
          </FlexContainer>
          {!theme.isLargeScreen && `${"\n"}`}
          {userInfo.user.plan_details?.plan?.is_trial
            ? !userInfo.isEnrolledTrial() &&
              !theme.isLargeScreen &&
              (userInfo.isUpcomingNotPaidTrialPlan() && !planExpired
                ? messageParts[0].split("! ")[1]
                : userInfo.isUpcomingNotPaidTrialPlan() && planExpired
                ? messageParts[0].split("Ended -")[1]
                : planExpired
                ? messageParts[0].split("— ")[1]
                : remainingTextPartsMobile.split("{{link}}")[0])
            : null}
          <StyledLinkText bannerType={bannerType} onPress={handleLinkPress}>
            {userInfo.isEnrolledTrial() && !theme.isLargeScreen
              ? t(
                  "banner.freeTrial.profileSection.web.enrolled.text",
                  "profile"
                )
              : userInfo.isUpcomingNotPaidTrialPlan()
              ? t(
                  "banner.freeTrial.profileSection.web.upcoming.text",
                  "click here"
                )
              : planExpired && currentPlan?.is_trial
              ? t(
                  "banner.freeTrial.profileSection.web.expired.text",
                  "Profile section"
                )
              : userInfo?.isInExpiryBucketButUpcomingSubscription()
              ? userInfo.getUpcomingSubscription()?.plan_duration === 2
                ? t("banner.renewal.link.web.twoYear.text", "click here")
                : t(
                    "banner.renewal.link.web.oneYear.text",
                    "Save 15% with a 2-Year Plan"
                  )
              : userInfo.isMembershipExpired()
              ? t(
                  "banner.expired.membership.profileSection.web.text",
                  "Purchase Plan"
                )
              : t(
                  "banner.freeTrial.profileSection.web.text",
                  "visit the profile section"
                )}
          </StyledLinkText>
          {messageParts[1]}
        </VDBText>
      );
    };
    if (!isWeb) {
      return <MobileContent />;
    }
    return message && message.includes("{{link}}") ? (
      <WebContent />
    ) : (
      <VDBText color={bodyColor} type={typography["Body-M"]}>
        {Platform.OS !== "web"
          ? message?.replace(" Free Trial", " Free Trial\n")
          : message}
      </VDBText>
    );
  };

  const BannerContent = () => {
    // LinkedBannerRow brings its own icon, spacing and padding.
    if (hasBannerLink(message)) {
      return <TextContent />;
    }
    return (
      <Container width={width}>
        {(theme.isLargeScreen ||
          (!theme.isLargeScreen && !isAdmin && isWeb)) && (
          <VDBIcon
            variant={IconsVariant.VDBCustom}
            name={VDBIcons.WARNING}
            color={iconColor}
            size={24}
          />
        )}
        <TextContent />
      </Container>
    );
  };
  return (
    <Banner
      bgColor={bannerType === "error" ? theme["error-01"] : theme["warn-02"]}
      children={<BannerContent />}
    />
  );
};

const Container = styled.View`
  width: ${({ width, theme }: { width: number; theme: DefaultTheme }) =>
    theme.isMobile ? width - 60 : width}px;
  flex-direction: row;
  ${({ theme }: { theme: DefaultTheme }) =>
    !theme.isLargeScreen ? "" : "align-items: center;"}
  justify-content: center;
  gap: ${SpaceType.Basic};
  align-items: center;
  padding-vertical: ${SpaceType.Basic};
`;

const StyledLinkText = styled.Text<{ bannerType: string }>`
  color: ${({
    theme,
    bannerType,
  }: {
    theme: DefaultTheme;
    bannerType: string;
  }) => (bannerType === "error" ? theme["core-01"] : theme["link-static"])};
  text-decoration-line: ${({ bannerType }: { bannerType: string }) =>
    bannerType === "error" ? "underline" : "none"};
`;
const MobileContainer = styled.View`
  justify-content: flex-start;
`;
const FlexContainer = styled.View`
  flex-direction: row;
  align-items: center;
  justify-content: center;
`;

const LinkedBannerRow = styled.View<{ alignLeft: boolean }>`
  width: 100%;
  flex-direction: row;
  align-items: center;
  justify-content: ${({ alignLeft }: { alignLeft: boolean }) =>
    alignLeft ? "flex-start" : "center"};
  gap: ${SpaceType.Basic}px;
  padding: ${SpaceType.Basic}px ${SpaceType.Double}px;
`;

const LinkedBannerTextArea = styled.View<{ alignLeft: boolean }>`
  ${({ alignLeft }: { alignLeft: boolean }) =>
    alignLeft ? "flex: 1;" : "flex-shrink: 1;"}
`;

export default memo(FreeTrialBanner);
